import { useState } from "react"
import {
  CheckCircle,
  Clock,
  DotsHorizontal,
  Mail01,
  Send01,
  Trash01,
} from "@untitledui/icons"
import { toast } from "sonner"
import {
  useInviteMember,
  useRevokeInvite,
  useResendInvite,
  useTeamInvites,
  useTeamMembers,
} from "@/lib/api/use-teams"
import { ApiError } from "@/lib/api/client"
import type { ApiTeamMemberInvite, TeamType } from "@/lib/api/types"
import { useTeamPermissions } from "@/lib/team/use-team-permissions"
import { DesignMenu } from "@/components/ui/design-menu"
import { InviteMemberDialog } from "./invite-member-dialog"

// Compact members table designed to live inside an expanded org-chart card.
// Same data as the full TeamMembersTable but with tighter chrome (smaller
// header, smaller rows). Action menu is invite-row-only — Resend / Revoke.
// Member-row management is the full table's job; the mini view is a
// summary that complements it.

function initialsOf(s: string): string {
  return s
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase()
}

interface TeamMembersTableMiniProps {
  teamId: number
  teamName: string
  teamType?: TeamType
}

export function TeamMembersTableMini({
  teamId,
  teamName,
  teamType,
}: TeamMembersTableMiniProps) {
  const perms = useTeamPermissions(teamId)
  const resendMutation = useResendInvite(teamId)
  const revokeMutation = useRevokeInvite(teamId)
  const { data: members = [], isLoading: membersLoading } = useTeamMembers(teamId)
  const { data: pendingInvites = [], error: invitesError } = useTeamInvites(teamId)
  const invitesUnavailable = (invitesError as ApiError | undefined)?.status === 404

  const [inviteOpen, setInviteOpen] = useState(false)

  const handleResend = async (invite: ApiTeamMemberInvite) => {
    try {
      await resendMutation.mutateAsync(invite.uuid)
      toast.success(`Invite resent to ${invite.user.email}`)
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Couldn't resend invite"
      toast.error(msg)
    }
  }

  const handleRevoke = async (invite: ApiTeamMemberInvite) => {
    try {
      await revokeMutation.mutateAsync(invite.uuid)
      toast.success(`Invite for ${invite.user.email} revoked`)
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Couldn't revoke invite"
      toast.error(msg)
    }
  }

  const total = members.length + (invitesUnavailable ? 0 : pendingInvites.length)

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500">
          Team members{" "}
          {total > 0 && (
            <span className="text-zinc-400 font-semibold ml-1">{total}</span>
          )}
        </p>
        {perms.canInvite && (
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg text-[11.5px] font-semibold text-zinc-700 border border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50 transition-colors duration-150"
          >
            <Send01 className="size-3" aria-hidden />
            Invite
          </button>
        )}
      </div>

      {membersLoading ? (
        <p className="text-[12px] text-zinc-400">Loading…</p>
      ) : total === 0 ? (
        <p className="text-[12px] text-zinc-500">No one on this team yet.</p>
      ) : (
        <div className="rounded-xl border border-zinc-200 bg-white divide-y divide-zinc-100 overflow-hidden">
          {members.map((m) => {
            const name =
              [m.firstName, m.lastName].filter(Boolean).join(" ").trim() || m.email
            const fallbackChar = m.email[0]?.toUpperCase() ?? "·"
            const avatarText = initialsOf(name) || fallbackChar
            return (
              <div
                key={`m-${m.email.toLowerCase()}`}
                className="flex items-center gap-2.5 px-3 py-2"
              >
                <div className="size-8 rounded-lg bg-zinc-100 text-zinc-700 grid place-items-center text-[11px] font-bold shrink-0">
                  {avatarText}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-semibold text-zinc-900 truncate">{name}</p>
                  <p className="text-[11px] text-zinc-500 truncate">{m.email}</p>
                </div>
                <span className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-zinc-100 text-zinc-600 whitespace-nowrap">
                  {m.role}
                </span>
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-emerald-50 text-emerald-700 whitespace-nowrap">
                  <CheckCircle className="size-2.5" aria-hidden />
                  Active
                </span>
              </div>
            )
          })}
          {!invitesUnavailable &&
            pendingInvites.map((invite) => {
              const name =
                [invite.user.firstName, invite.user.lastName]
                  .filter(Boolean)
                  .join(" ")
                  .trim() || invite.user.email
              return (
                <div
                  key={`i-${invite.uuid}`}
                  className="flex items-center gap-2.5 px-3 py-2"
                >
                  <div className="size-8 rounded-lg bg-amber-50 text-amber-700 ring-1 ring-amber-200/70 grid place-items-center shrink-0">
                    <Mail01 className="size-3.5" aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-zinc-900 truncate">{name}</p>
                    <p className="text-[11px] text-zinc-500 truncate">{invite.user.email}</p>
                  </div>
                  <span className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-zinc-100 text-zinc-600 whitespace-nowrap">
                    {invite.role}
                  </span>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-amber-50 text-amber-700 whitespace-nowrap">
                    <Clock className="size-2.5" aria-hidden />
                    Pending
                  </span>
                  {perms.canManageMembers && (
                    <DesignMenu.Root>
                      <DesignMenu.Trigger asChild>
                        <button
                          type="button"
                          className="size-7 rounded-lg grid place-items-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors duration-150"
                          aria-label={`Actions for invite to ${invite.user.email}`}
                        >
                          <DotsHorizontal className="size-3.5" aria-hidden />
                        </button>
                      </DesignMenu.Trigger>
                      <DesignMenu.Content align="end">
                        <DesignMenu.Item
                          onSelect={() => handleResend(invite)}
                          icon={<Send01 className="text-zinc-500" aria-hidden />}
                        >
                          Resend invite
                        </DesignMenu.Item>
                        <DesignMenu.Separator />
                        <DesignMenu.Item
                          destructive
                          onSelect={() => handleRevoke(invite)}
                          icon={<Trash01 aria-hidden />}
                        >
                          Revoke
                        </DesignMenu.Item>
                      </DesignMenu.Content>
                    </DesignMenu.Root>
                  )}
                </div>
              )
            })}
        </div>
      )}

      <InviteMemberDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        teamId={teamId}
        teamName={teamName}
        teamType={teamType ?? "Dealership"}
      />
    </div>
  )
}
